import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import Landing from "@/pages/Landing";
import Home from "@/pages/Home";
import CompanyDetail from "@/pages/CompanyDetail";
import AdminLogin from "@/pages/AdminLogin";
import AdminDashboard from "@/pages/AdminDashboard";
import AdminCompanies from "@/pages/AdminCompanies";
import AdminCoupons from "@/pages/AdminCoupons";
import AdminStatistics from "@/pages/AdminStatistics";
import { AuthProvider } from "@/context/AuthContext";
import ErrorBoundary from "@/components/ErrorBoundary";

// ---- App version-based cache busting ----
// Bump APP_VERSION whenever a major deploy might change chunk hashes / routes.
// On version change we force a Service Worker update + hard reload once.
const APP_VERSION = '2026.02.05';

const handleAppVersion = () => {
  try {
    const stored = localStorage.getItem('appVersion');
    if (stored !== APP_VERSION) {
      localStorage.setItem('appVersion', APP_VERSION);
      // Clear stale caches once on version change
      if ('caches' in window) {
        caches.keys().then((names) => {
          names.forEach((n) => caches.delete(n));
        }).catch(() => {});
      }
    }
  } catch (e) {
    // localStorage unavailable - ignore
  }
};
handleAppVersion();

// ---- Service worker registration with auto-update ----
if ('serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    navigator.serviceWorker
      .register('/sw.js', { updateViaCache: 'none' })
      .then((reg) => {
        // Check for updates on every load
        reg.update().catch(() => {});

        reg.addEventListener('updatefound', () => {
          const installing = reg.installing;
          if (!installing) return;
          installing.addEventListener('statechange', () => {
            if (
              installing.state === 'installed' &&
              navigator.serviceWorker.controller
            ) {
              // New SW available - activate immediately
              installing.postMessage({ type: 'SKIP_WAITING' });
            }
          });
        });
      })
      .catch(() => {});

    let reloaded = false;
    navigator.serviceWorker.addEventListener('controllerchange', () => {
      if (reloaded) return;
      reloaded = true;
      window.location.reload();
    });
  });
}

// ---- Chunk-load error recovery ----
// If a hashed JS chunk fails to load (common after a redeploy with stale cache),
// reload the page once to fetch fresh chunks. Prevents the dreaded blank page.
const tryReloadOnce = () => {
  try {
    const key = 'chunkReloadAt';
    const last = Number(sessionStorage.getItem(key) || 0);
    const now = Date.now();
    if (now - last > 10000) {
      sessionStorage.setItem(key, String(now));
      window.location.reload();
    }
  } catch (e) {
    window.location.reload();
  }
};

window.addEventListener('error', (e) => {
  const msg = e && e.message ? String(e.message) : '';
  if (
    msg.includes('ChunkLoadError') ||
    msg.includes('Loading chunk') ||
    msg.includes("Failed to fetch dynamically imported module")
  ) {
    tryReloadOnce();
  }
});

window.addEventListener('unhandledrejection', (e) => {
  const reason = e && e.reason ? String(e.reason && (e.reason.message || e.reason)) : '';
  if (
    reason.includes('ChunkLoadError') ||
    reason.includes('Loading chunk') ||
    reason.includes("Failed to fetch dynamically imported module")
  ) {
    tryReloadOnce();
  }
});

// Protected route for employees
const ProtectedRoute = ({ children }) => {
  const access = localStorage.getItem('employeeAccess');
  if (!access) {
    return <Navigate to="/" replace />;
  }
  return children;
};

function App() {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <BrowserRouter>
          <div className="App app-container">
            <Routes>
              <Route path="/" element={<Landing />} />
              <Route path="/ofertas" element={<ProtectedRoute><Home /></ProtectedRoute>} />
              <Route path="/empresa/:id" element={<ProtectedRoute><CompanyDetail /></ProtectedRoute>} />
              <Route path="/admin" element={<AdminLogin />} />
              <Route path="/admin/dashboard" element={<AdminDashboard />} />
              <Route path="/admin/empresas" element={<AdminCompanies />} />
              <Route path="/admin/cupones" element={<AdminCoupons />} />
              <Route path="/admin/estadisticas" element={<AdminStatistics />} />
            </Routes>
          </div>
        </BrowserRouter>
      </AuthProvider>
    </ErrorBoundary>
  );
}

export default App;
